/**
 * RingGroupManager — wie rinkelt er mee bij een inkomend gesprek.
 *
 * Praat met /api/telephony/users: per office-gebruiker een eigen Telnyx-
 * credential provisionen/intrekken, ring aan/uit, live presence-status.
 */

import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { supabase } from '../../../lib/supabase';

interface RingUser {
  userId: string;
  email: string;
  displayName: string | null;
  role: string | null;
  provisioned: boolean;
  sipUsername: string | null;
  ringEnabled: boolean;
  presence: string;
  credentialExpired: boolean | null;
  lastRegisteredAt: string | null;
  registrationFresh: boolean;
}

/** "5 min geleden" / "3 uur geleden" voor de registratie-status. */
function agoLabel(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (mins < 60) return `${mins} min geleden`;
  const hours = Math.round(mins / 60);
  return `${hours} uur geleden`;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

export function RingGroupManager() {
  const [users, setUsers] = useState<RingUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await fetch('/api/telephony/users', { headers: await authHeaders() });
      if (!r.ok) throw new Error(`users failed: ${r.status}`);
      const json = await r.json();
      setUsers(json.users ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  const provision = useCallback(
    async (u: RingUser) => {
      setBusyUserId(u.userId);
      setError(null);
      try {
        const r = await fetch('/api/telephony/users', {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify({ userId: u.userId }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({ error: 'Provisionen mislukt' }));
          throw new Error(err.error || 'Provisionen mislukt');
        }
        await fetchUsers();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyUserId(null);
      }
    },
    [fetchUsers],
  );

  const toggleRing = useCallback(
    async (u: RingUser) => {
      setBusyUserId(u.userId);
      setError(null);
      try {
        const r = await fetch('/api/telephony/users', {
          method: 'PATCH',
          headers: await authHeaders(),
          body: JSON.stringify({ userId: u.userId, ringEnabled: !u.ringEnabled }),
        });
        if (!r.ok) throw new Error('Wijzigen mislukt');
        await fetchUsers();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyUserId(null);
      }
    },
    [fetchUsers],
  );

  const revoke = useCallback(
    async (u: RingUser) => {
      if (!window.confirm(`Belregistratie van ${u.displayName || u.email} intrekken?`)) return;
      setBusyUserId(u.userId);
      setError(null);
      try {
        const r = await fetch(`/api/telephony/users?userId=${encodeURIComponent(u.userId)}`, {
          method: 'DELETE',
          headers: await authHeaders(),
        });
        if (!r.ok) throw new Error('Intrekken mislukt');
        await fetchUsers();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyUserId(null);
      }
    },
    [fetchUsers],
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{error}</div>
      )}
      <p className="text-xs text-slate-500">
        De ring group geldt voor alle merken: hetzelfde team neemt elk merk-nummer op.
        Geprovisionede gebruikers registreren onder een eigen identiteit en rinkelen tegelijk mee (eerste die
        opneemt wint). Zonder geprovisionede gebruikers rinkelt de gedeelde registratie (huidig gedrag).
      </p>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-12 bg-slate-50 rounded animate-pulse" />
          ))}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {users.map((u) => (
            <li key={u.userId} className="py-3 flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={clsx(
                      'w-2 h-2 rounded-full',
                      !u.provisioned ? 'bg-slate-300' : u.presence === 'dnd' ? 'bg-amber-500' : 'bg-emerald-500',
                    )}
                    aria-hidden
                  />
                  <span className="text-sm font-medium text-slate-800">{u.displayName || u.email}</span>
                  {u.role && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{u.role}</span>
                  )}
                  {u.credentialExpired && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">credential verlopen</span>
                  )}
                  {u.provisioned && u.ringEnabled && !u.registrationFresh && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700"
                      title="Rinkelt mee in de instellingen, maar de softphone is nergens (vers) geregistreerd — inkomende gesprekken bereiken deze gebruiker niet. Open de office-app (of herlaad de tab) op het apparaat dat moet rinkelen."
                    >
                      niet geregistreerd
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {u.provisioned
                    ? `${u.presence === 'dnd' ? 'Afwezig' : 'Beschikbaar'} · ${u.ringEnabled ? 'in ring group' : 'ring uit'}${
                        u.lastRegisteredAt ? ` · geregistreerd ${agoLabel(u.lastRegisteredAt)}` : ''
                      }`
                    : 'Nog geen eigen belregistratie'}
                </div>
              </div>

              {u.provisioned ? (
                <>
                  <label className="flex items-center gap-1.5 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={u.ringEnabled}
                      disabled={busyUserId === u.userId}
                      onChange={() => void toggleRing(u)}
                    />
                    Rinkelt mee
                  </label>
                  <button
                    type="button"
                    onClick={() => void revoke(u)}
                    disabled={busyUserId === u.userId}
                    className="text-xs text-slate-400 hover:text-red-600"
                  >
                    Intrekken
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => void provision(u)}
                  disabled={busyUserId === u.userId}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded"
                >
                  {busyUserId === u.userId ? 'Bezig…' : 'Provisionen'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
